/**
 * Removes identifying metadata from image bytes without touching the pixels.
 *
 * Phone cameras embed a lot more than orientation: GPS coordinates, the device make/model and
 * serial, lens data, and capture timestamps all ride along in EXIF, and editors add XMP/IPTC blocks
 * carrying author and copyright fields. Anything uploaded to a profile or a community post is served
 * back publicly, so that metadata has to come off before the bytes leave the browser.
 *
 * These helpers rewrite the container's segment/chunk list rather than re-encoding through a canvas:
 * the compressed image data is copied verbatim, so there is no generation loss and the file size
 * barely moves (which matters — the uploader reports the size to the presign endpoint for quota
 * accounting before the bytes go up).
 *
 * The one thing deliberately preserved is EXIF `Orientation`. Dropping it would leave phone photos
 * displaying sideways, since the pixels really are stored rotated. When the source declares a
 * non-default orientation, a minimal EXIF block containing only that tag is written back.
 *
 * Formats other than JPEG and PNG are returned untouched.
 *
 * The `Uint8Array<ArrayBuffer>` signature is deliberate rather than a bare `Uint8Array`: callers wrap the
 * result in a `Blob`/`File`, and a `SharedArrayBuffer`-backed view is not a valid `BlobPart`.
 */

const EXIF_ID = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"
const JFIF_ID = [0x4a, 0x46, 0x49, 0x46, 0x00]; // "JFIF\0"
const ICC_ID = [0x49, 0x43, 0x43, 0x5f, 0x50, 0x52, 0x4f, 0x46, 0x49, 0x4c, 0x45]; // "ICC_PROFILE"
const ADOBE_ID = [0x41, 0x64, 0x6f, 0x62, 0x65]; // "Adobe"

/** PNG chunks that carry text, EXIF or timestamps. Everything else (colour, transparency, animation) stays. */
const PNG_DROP_CHUNKS = ["tEXt", "zTXt", "iTXt", "eXIf", "tIME"];

export function isJpegBytes(bytes: Uint8Array): boolean {
  return bytes.byteLength > 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

export function isPngBytes(bytes: Uint8Array): boolean {
  return bytes.byteLength > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a;
}

/**
 * Strips location/identifying metadata from a JPEG or PNG, preserving orientation and pixel data.
 * Returns the input unchanged for any other format, or if the bytes turn out to be malformed.
 */
export function stripImageMetadata(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  try {
    if (isJpegBytes(bytes)) return stripJpegMetadata(bytes);
    if (isPngBytes(bytes)) return stripPngMetadata(bytes);
  } catch (e) {
    // A file we can't parse is a file we shouldn't rewrite — hand back the original.
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// JPEG
// ---------------------------------------------------------------------------

function stripJpegMetadata(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const kept: Uint8Array[] = [bytes.subarray(0, 2)]; // SOI
  let orientation: number | null = null;
  let leadingApp0 = false;
  let i = 2;

  while (i + 4 <= bytes.byteLength) {
    if (bytes[i] !== 0xff) break; // desynced — stop rewriting and copy what's left
    const marker = bytes[i + 1];

    if (marker === 0xff) {
      i++; // fill byte
      continue;
    }
    // Standalone markers carry no length field.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      kept.push(bytes.subarray(i, i + 2));
      i += 2;
      continue;
    }

    const length = view.getUint16(i + 2);
    const segmentEnd = i + 2 + length;
    if (length < 2 || segmentEnd > bytes.byteLength) break;

    // Start of scan: everything from here on is entropy-coded image data.
    if (marker === 0xda) {
      kept.push(bytes.subarray(i));
      i = bytes.byteLength;
      break;
    }

    const payload = bytes.subarray(i + 4, segmentEnd);
    if (isDroppableJpegSegment(marker, payload)) {
      if (marker === 0xe1 && startsWith(payload, EXIF_ID)) {
        orientation = readTiffOrientation(bytes, view, i + 4 + EXIF_ID.length);
      }
    } else {
      if (marker === 0xe0 && kept.length === 1) leadingApp0 = true;
      kept.push(bytes.subarray(i, segmentEnd));
    }
    i = segmentEnd;
  }

  if (i < bytes.byteLength) kept.push(bytes.subarray(i));

  if (orientation !== null && orientation > 1 && orientation <= 8) {
    // EXIF belongs at the front, after a JFIF APP0 if the file had one.
    kept.splice(leadingApp0 ? 2 : 1, 0, buildExifApp1Segment(orientation));
  }

  return concatBytes(kept);
}

function isDroppableJpegSegment(marker: number, payload: Uint8Array): boolean {
  switch (marker) {
    case 0xe0: // APP0 — keep the JFIF header, drop the JFXX thumbnail
      return !startsWith(payload, JFIF_ID);
    case 0xe1: // APP1 — EXIF and XMP
      return true;
    case 0xe2: // APP2 — keep colour profiles, drop MPF/FlashPix side images
      return !startsWith(payload, ICC_ID);
    case 0xee: // APP14 — Adobe colour transform, needed to decode correctly
      return !startsWith(payload, ADOBE_ID);
    case 0xed: // APP13 — Photoshop IRB / IPTC
      return true;
    case 0xfe: // COM — free-text comment
      return true;
    default:
      // Remaining APPn slots are vendor maker-note space; structural markers are kept.
      return marker >= 0xe3 && marker <= 0xef;
  }
}

/** A complete APP1 segment holding an EXIF block whose only tag is Orientation. */
function buildExifApp1Segment(orientation: number): Uint8Array<ArrayBuffer> {
  const tiff = buildOrientationTiff(orientation);
  const segment = new Uint8Array(4 + EXIF_ID.length + tiff.byteLength);
  const length = 2 + EXIF_ID.length + tiff.byteLength;
  segment[0] = 0xff;
  segment[1] = 0xe1;
  segment[2] = (length >> 8) & 0xff;
  segment[3] = length & 0xff;
  segment.set(EXIF_ID, 4);
  segment.set(tiff, 4 + EXIF_ID.length);
  return segment;
}

/** Big-endian TIFF block: header + a one-entry IFD0 containing only Orientation. */
function buildOrientationTiff(orientation: number): Uint8Array<ArrayBuffer> {
  return new Uint8Array([
    0x4d,
    0x4d,
    0x00,
    0x2a,
    0x00,
    0x00,
    0x00,
    0x08, // "MM", 42, IFD0 at offset 8
    0x00,
    0x01, // one entry
    0x01,
    0x12,
    0x00,
    0x03,
    0x00,
    0x00,
    0x00,
    0x01, // tag 0x0112, SHORT, count 1
    (orientation >> 8) & 0xff,
    orientation & 0xff,
    0x00,
    0x00,
    0x00,
    0x00,
    0x00,
    0x00, // no next IFD
  ]);
}

/** Reads the Orientation tag out of the TIFF block starting at `tiffStart`, or null if absent. */
function readTiffOrientation(bytes: Uint8Array, view: DataView, tiffStart: number): number | null {
  // `view` spans exactly `bytes`, so TIFF offsets index both the same way.
  const at = tiffStart;
  if (at + 8 > bytes.byteLength) return null;

  const byteOrder = view.getUint16(at);
  let little: boolean;
  if (byteOrder === 0x4949) little = true;
  else if (byteOrder === 0x4d4d) little = false;
  else return null;

  if (view.getUint16(at + 2, little) !== 0x002a) return null;

  const ifd0 = at + view.getUint32(at + 4, little);
  if (ifd0 + 2 > bytes.byteLength) return null;

  const entries = view.getUint16(ifd0, little);
  for (let e = 0; e < entries; e++) {
    const entry = ifd0 + 2 + e * 12;
    if (entry + 12 > bytes.byteLength) return null;
    if (view.getUint16(entry, little) === 0x0112) {
      return view.getUint16(entry + 8, little);
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// PNG
// ---------------------------------------------------------------------------

function stripPngMetadata(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const kept: Uint8Array[] = [bytes.subarray(0, 8)]; // signature
  let orientation: number | null = null;
  let sawEnd = false;
  let i = 8;

  while (i + 12 <= bytes.byteLength) {
    const length = view.getUint32(i);
    const type = String.fromCharCode(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7]);
    const chunkEnd = i + 12 + length;
    if (chunkEnd > bytes.byteLength) break;

    if (PNG_DROP_CHUNKS.indexOf(type) > -1) {
      if (type === "eXIf") orientation = readTiffOrientation(bytes, view, i + 8);
    } else {
      kept.push(bytes.subarray(i, chunkEnd));
    }

    i = chunkEnd;
    if (type === "IEND") {
      sawEnd = true;
      break;
    }
  }

  if (!sawEnd) return bytes; // truncated or non-standard — leave it alone

  if (orientation !== null && orientation > 1 && orientation <= 8) {
    kept.splice(kept.length - 1, 0, buildPngChunk("eXIf", buildOrientationTiff(orientation)));
  }

  return concatBytes(kept);
}

function buildPngChunk(type: string, data: Uint8Array): Uint8Array<ArrayBuffer> {
  const chunk = new Uint8Array(12 + data.byteLength);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.byteLength);
  for (let c = 0; c < 4; c++) chunk[4 + c] = type.charCodeAt(c);
  chunk.set(data, 8);
  view.setUint32(8 + data.byteLength, crc32(chunk.subarray(4, 8 + data.byteLength)));
  return chunk;
}

let crcTable: Uint32Array | null = null;
function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let c = 0xffffffff;
  for (let i = 0; i < bytes.byteLength; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

// ---------------------------------------------------------------------------

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
  if (bytes.byteLength < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (bytes[i] !== prefix[i]) return false;
  }
  return true;
}

function concatBytes(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  let total = 0;
  for (const part of parts) total += part.byteLength;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.byteLength;
  }
  return out;
}
